import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShoppingBag } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { StoreOrderSummary } from '@aeris/contracts'
import { formatMoney, fromString } from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
// Os textos ao cliente moram em `@aeris/status-meta` desde a F307: o celular
// avisa com as mesmas palavras.
import {
  STORE_ORDER_REJECT_PRESETS,
  STORE_ORDER_SKIP_REASONS,
  storeOrderMessage,
} from '@aeris/status-meta'
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  Input,
  Page,
  PageHeader,
  Section,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  notify,
} from '@aeris/ui'

import { AuthorisationDialog } from '../components/authorisation-dialog.tsx'
import { LineThumb } from '../components/line-thumb.tsx'
import { LoadFailed } from '../components/load-failed.tsx'
import {
  ApiError,
  approveStoreOrder,
  fetchSalesDocument,
  fetchStoreOrders,
  rejectStoreOrder,
  transitionSalesDocument,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { formatDayTime } from '../lib/date-format.ts'
import { whatsappLink } from '../lib/whatsapp-link.ts'
import { useWorkspace } from '../lib/workspace.ts'

/**
 * Os pedidos da loja online — F224.
 *
 * O cliente fechou o carrinho no site e mandou o pedido no WhatsApp da
 * vendedora. Aqui ela confere o estoque de agora, diz quanto vai de cada peça
 * (zero tira a peça; menos que o pedido é o "aprovado com alterações") e
 * aprova — nasce o pedido de venda confirmado, para separar —, ou recusa com
 * o motivo, que o cliente vê. Nas duas, o cliente é avisado no WhatsApp pelo
 * número dela, se já conversou nele.
 */

const money = (value: string): string => formatMoney(fromString(value))

/** "2.000000" → "2". */
const plain = (value: string): string =>
  value.includes('.') ? value.replace(/\.?0+$/, '') : value

const STATUS_LABELS: Record<StoreOrderSummary['status'], string> = {
  pending: 'Esperando',
  approved: 'Aprovado',
  rejected: 'Recusado',
  cancelled: 'Cancelado pelo cliente',
}

const STATUS_TONES: Record<
  StoreOrderSummary['status'],
  'neutral' | 'success' | 'danger'
> = {
  pending: 'neutral',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
}

const time = (iso: string): string => formatDayTime(iso)

export const PedidosLojaPage = (): ReactNode => {
  const { allows } = useAuth()
  const canDecide = allows('sales.write')
  const [state, setState] = useState<'pending' | 'decided'>('pending')
  const [mine, setMine] = useState(true)

  const orders = useQuery({
    queryKey: ['store-orders', state, mine],
    queryFn: async ({ signal }) =>
      await fetchStoreOrders({ state, mine }, signal),
    // A fila anda sozinha: o pedido chega pelo site, não por esta tela.
    refetchInterval: 30_000,
  })

  const items = orders.data?.orders ?? []

  return (
    <Page>
      <PageHeader
        title="Pedidos da loja"
        description="O que o cliente pediu no site. Confira o estoque, aprove — vira pedido para separar — ou recuse com o motivo."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select
          aria-label="Situação"
          className="w-44"
          value={state}
          onChange={(event) => {
            setState(event.target.value as 'pending' | 'decided')
          }}
        >
          <option value="pending">Esperando</option>
          <option value="decided">Decididos</option>
        </Select>
        <Select
          aria-label="De quem"
          className="w-44"
          value={mine ? 'mine' : 'all'}
          onChange={(event) => {
            setMine(event.target.value === 'mine')
          }}
        >
          <option value="mine">Só os meus</option>
          <option value="all">Todos</option>
        </Select>
      </div>

      {orders.isPending ? (
        <Spinner label="Carregando os pedidos" />
      ) : orders.isError ? (
        <LoadFailed
          what="os pedidos da loja"
          onRetry={() => {
            void orders.refetch()
          }}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<ShoppingBag aria-hidden="true" className="h-8 w-8" />}
          title={
            state === 'pending'
              ? 'Nenhum pedido esperando'
              : 'Nenhum pedido decidido'
          }
          description={
            mine
              ? 'Mostrando só os pedidos em que o cliente escolheu você. Troque para "Todos" para ver os da loja.'
              : 'Quando um cliente fechar o carrinho no site, o pedido aparece aqui.'
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((order) =>
            order.status === 'pending' ? (
              <PendingOrder
                key={`${order.id}:${String(order.version)}`}
                order={order}
                canDecide={canDecide}
              />
            ) : (
              <DecidedOrder
                key={order.id}
                order={order}
                canCancel={allows('sales.cancel') || allows('finance.settle')}
              />
            )
          )}
        </div>
      )}
    </Page>
  )
}

const customerOf = (order: StoreOrderSummary): string =>
  order.customerCode === null
    ? order.customerName
    : `${order.customerName} (cód. ${order.customerCode})`

/**
 * O WhatsApp do cliente com a mensagem pronta, para quando o aviso automático
 * não saiu (F248) — em vez de só "avise você", o botão que avisa.
 */
const chatAbout = (
  order: StoreOrderSummary,
  text: string
): (() => void) | undefined => {
  const link = whatsappLink(order.customerPhone, text)
  return link === null
    ? undefined
    : () => {
        window.open(link, '_blank', 'noopener')
      }
}

/** O aviso para a vendedora: o que aconteceu, e se o cliente foi avisado. */
const announce = (
  after: StoreOrderSummary,
  done: string,
  openSales: () => void
): void => {
  notify.success(done, {
    ...(after.status === 'approved'
      ? { action: { label: 'Ver em Vendas', onClick: openSales } }
      : {}),
    duration: 8000,
  })
  if (after.customerNotifiedAt !== null) return
  const chat = chatAbout(after, storeOrderMessage(after))
  notify.info(
    `O aviso ao cliente não saiu: ${STORE_ORDER_SKIP_REASONS[after.notifySkipReason ?? ''] ?? 'avise o cliente pelo WhatsApp.'}`,
    {
      duration: 20_000,
      ...(chat === undefined
        ? {}
        : { action: { label: 'Abrir o WhatsApp', onClick: chat } }),
    }
  )
}

const PendingOrder = ({
  order,
  canDecide,
}: {
  readonly order: StoreOrderSummary
  readonly canDecide: boolean
}): ReactNode => {
  const queryClient = useQueryClient()
  const workspace = useWorkspace()
  const [confirming, setConfirming] = useState(false)
  // O que vai de cada peça: abre com o que o cliente pediu.
  const [going, setGoing] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      order.lines.map((line) => [line.id, plain(line.quantity)])
    )
  )
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const failure = (caught: unknown): void => {
    setError(
      caught instanceof ApiError
        ? caught.message
        : 'Não foi possível concluir. Tente de novo.'
    )
  }

  const openSales = (): void => {
    workspace.open({ module: 'vendas' })
  }

  /** O que vai, ao preço que o cliente viu — o servidor cobra o de agora. */
  const going_ = order.lines.map((line) => ({
    line,
    quantity: Number((going[line.id] ?? '0').replace(',', '.').trim() || '0'),
  }))
  const pieces = going_.reduce(
    (sum, entry) =>
      sum + (Number.isFinite(entry.quantity) ? entry.quantity : 0),
    0
  )
  const estimate = going_.reduce(
    (sum, entry) =>
      sum +
      (Number.isFinite(entry.quantity)
        ? entry.quantity * Number(entry.line.unitPrice)
        : 0),
    0
  )

  const approve = useMutation({
    mutationFn: async () =>
      await approveStoreOrder(order.id, {
        version: order.version,
        lines: order.lines.map((line) => ({
          lineId: line.id,
          quantity: (going[line.id] ?? '0').replace(',', '.').trim() || '0',
        })),
      }),
    onSuccess: async ({ order: after }) => {
      setConfirming(false)
      announce(
        after,
        after.salesDocumentNumber === null
          ? `Pedido nº ${String(after.number)} aprovado: ${money(after.approvedAmount ?? after.totalAmount)}. O pedido de venda está em Vendas para separar.`
          : `Pedido nº ${String(after.number)} aprovado: ${money(after.approvedAmount ?? after.totalAmount)}. Em Vendas, é o pedido nº ${formatDocumentNumber(after.salesDocumentNumber)}, para separar.`,
        openSales
      )
      await queryClient.invalidateQueries({ queryKey: ['store-orders'] })
    },
    onError: failure,
  })

  const reject = useMutation({
    mutationFn: async () =>
      await rejectStoreOrder(order.id, {
        version: order.version,
        reason: reason.trim(),
      }),
    onSuccess: async ({ order: after }) => {
      announce(after, `Pedido nº ${String(after.number)} recusado.`, openSales)
      await queryClient.invalidateQueries({ queryKey: ['store-orders'] })
    },
    onError: failure,
  })

  return (
    <Section
      title={`Pedido nº ${String(order.number)} — ${customerOf(order)}`}
      description={[
        `Pedido às ${time(order.createdAt)}`,
        order.sellerName === null ? null : `Vendedora: ${order.sellerName}`,
        order.note === null ? null : `Obs.: ${order.note}`,
      ]
        .filter((part) => part !== null)
        .join(' · ')}
    >
      <TableContainer>
        <Table aria-label={`Peças do pedido ${String(order.number)}`}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Peça</TableHeaderCell>
              <TableHeaderCell numeric>Pediu</TableHeaderCell>
              <TableHeaderCell numeric>Disponível</TableHeaderCell>
              <TableHeaderCell numeric>Preço</TableHeaderCell>
              <TableHeaderCell numeric>Vai</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.lines.map((line) => {
              const available =
                line.available === null ? '0' : plain(line.available)
              const short = Number(available) < Number(plain(line.quantity))
              return (
                <TableRow key={line.id}>
                  <TableCell>
                    {/* A foto, para separar a peça certa (F286). */}
                    <div className="flex items-center gap-3">
                      <LineThumb
                        url={line.thumbUrl}
                        description={line.description}
                      />
                      <div>
                        {line.description}
                        <span className="block text-(length:--text-xs) text-(--text-secondary)">
                          Cód. {line.code}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell numeric>{plain(line.quantity)}</TableCell>
                  <TableCell numeric>
                    {short ? (
                      <Badge tone="danger">{available}</Badge>
                    ) : (
                      available
                    )}
                  </TableCell>
                  <TableCell numeric>{money(line.unitPrice)}</TableCell>
                  <TableCell numeric>
                    <Input
                      aria-label={`Quanto vai de ${line.description}`}
                      className="w-20 text-right"
                      inputMode="decimal"
                      disabled={!canDecide}
                      value={going[line.id] ?? ''}
                      onChange={(event) => {
                        setGoing((current) => ({
                          ...current,
                          // A vírgula fica na tela; vira ponto só ao mandar.
                          [line.id]: event.target.value,
                        }))
                      }}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <p className="text-(length:--text-sm) text-(--text-secondary)">
        Total do pedido: {money(order.totalAmount)}. Ao aprovar, o preço é o da
        tabela de agora.
      </p>

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      {!canDecide ? null : rejecting ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {STORE_ORDER_REJECT_PRESETS.map((preset) => (
              <Button
                key={preset}
                size="sm"
                variant="ghost"
                onClick={() => {
                  setReason(preset)
                }}
              >
                {preset}
              </Button>
            ))}
          </div>
          <Input
            aria-label="Motivo da recusa"
            placeholder="O motivo, que o cliente vê"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value)
            }}
          />
          <div className="flex gap-2">
            <Button
              variant="danger"
              size="sm"
              loading={reject.isPending}
              disabled={reason.trim().length < 3}
              onClick={() => {
                setError(null)
                reject.mutate()
              }}
            >
              Recusar o pedido
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setRejecting(false)
              }}
            >
              Voltar
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setError(null)
              setConfirming(true)
            }}
          >
            Aprovar
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setRejecting(true)
            }}
          >
            Recusar
          </Button>
        </div>
      )}

      {/*
        Aprovar pede confirmação (F248): cria o pedido de venda e avisa o
        cliente no WhatsApp — duas coisas que não se desfazem com um clique.
      */}
      <ConfirmDialog
        open={confirming}
        onOpenChange={(next) => {
          if (!next) setConfirming(false)
        }}
        title={`Aprovar o pedido nº ${String(order.number)}?`}
        description={`${customerOf(order)}: ${pieces === 1 ? '1 peça' : `${String(pieces)} peças`}, cerca de ${formatMoney(fromString(estimate.toFixed(2)))} — o valor final é o da tabela de agora. Nasce o pedido de venda confirmado, para separar, e o cliente é avisado no WhatsApp pelo seu número, se já conversou nele.`}
        confirmLabel="Aprovar"
        tone="neutral"
        loading={approve.isPending}
        onConfirm={() => {
          approve.mutate()
        }}
      />
    </Section>
  )
}

const DecidedOrder = ({
  order,
  canCancel,
}: {
  readonly order: StoreOrderSummary
  /** `sales.cancel`: o mesmo direito do Cancelar em Vendas (F286). */
  readonly canCancel: boolean
}): ReactNode => {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // O pedido aprovado segura mercadoria: o servidor pede a senha do supervisor.
  const [authorising, setAuthorising] = useState(false)

  /*
   * Cancelar o pedido aprovado (F286): é cancelar o pedido de venda que ele
   * virou, pela mesma transição de Vendas. Se o cliente já pagou algo, o
   * servidor pergunta reembolso ou vale, e a mensagem manda para Vendas.
   */
  const salesDocumentId = order.salesDocumentId
  const cancel = useMutation({
    mutationFn: async (authorisationId: string | null) => {
      if (salesDocumentId === null) return
      const { document } = await fetchSalesDocument(salesDocumentId)
      await transitionSalesDocument(salesDocumentId, {
        version: document.version,
        to: 'cancelled',
        reason: `Pedido nº ${String(order.number)} da loja online cancelado`,
        authorisationId,
      })
    },
    onSuccess: async () => {
      setConfirming(false)
      setError(null)
      notify.success(`Pedido nº ${String(order.number)} cancelado.`)
      await queryClient.invalidateQueries({ queryKey: ['store-orders'] })
    },
    onError: (caught: unknown, authorisationId) => {
      setConfirming(false)
      if (
        caught instanceof ApiError &&
        caught.code === 'authorisation_required' &&
        authorisationId === null
      ) {
        setError(null)
        setAuthorising(true)
        return
      }
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Não foi possível cancelar. Tente de novo, ou cancele em Vendas.'
      )
    },
  })

  const cancellable =
    canCancel && order.status === 'approved' && salesDocumentId !== null

  return (
    <Section
      title={`Pedido nº ${String(order.number)} — ${customerOf(order)}`}
      description={[
        `Pedido às ${time(order.createdAt)}`,
        order.decidedAt === null
          ? null
          : `decidido às ${time(order.decidedAt)}`,
        order.sellerName === null ? null : `Vendedora: ${order.sellerName}`,
      ]
        .filter((part) => part !== null)
        .join(' · ')}
    >
      <div className="flex flex-wrap items-center gap-2 text-(length:--text-sm)">
        <Badge tone={STATUS_TONES[order.status]}>
          {STATUS_LABELS[order.status]}
        </Badge>
        <span>
          {order.status === 'approved'
            ? `Aprovado: ${money(order.approvedAmount ?? order.totalAmount)} (pediu ${money(order.totalAmount)})`
            : order.status === 'rejected'
              ? `Motivo: ${order.rejectReason ?? ''}`
              : `Total: ${money(order.totalAmount)}`}
        </span>
        {order.status === 'approved' || order.status === 'rejected' ? (
          <span className="text-(--text-secondary)">
            {order.customerNotifiedAt === null
              ? `Aviso não saiu: ${STORE_ORDER_SKIP_REASONS[order.notifySkipReason ?? ''] ?? 'avise o cliente.'}`
              : 'Cliente avisado no WhatsApp.'}
          </span>
        ) : null}
        {order.status === 'approved' && order.salesDocumentNumber !== null ? (
          <span className="text-(--text-secondary)">
            Em Vendas: pedido nº{' '}
            {formatDocumentNumber(order.salesDocumentNumber)}
          </span>
        ) : null}
        {(order.status === 'approved' || order.status === 'rejected') &&
        order.customerNotifiedAt === null &&
        whatsappLink(order.customerPhone, storeOrderMessage(order)) !== null ? (
          <a
            href={
              whatsappLink(order.customerPhone, storeOrderMessage(order)) ?? ''
            }
            target="_blank"
            rel="noreferrer"
            className="font-(--weight-medium) text-(--brand-primary) hover:underline"
          >
            Avisar no WhatsApp
          </a>
        ) : null}
        {cancellable ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setConfirming(true)
            }}
          >
            Cancelar pedido
          </Button>
        ) : null}
      </div>
      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}
      <ConfirmDialog
        open={confirming}
        onOpenChange={(next) => {
          if (!next) setConfirming(false)
        }}
        title={`Cancelar o pedido nº ${String(order.number)}?`}
        description={`${customerOf(order)}: o pedido de venda é cancelado e a reserva volta ao estoque. O cliente vê o pedido como cancelado no site.`}
        confirmLabel="Cancelar pedido"
        tone="danger"
        loading={cancel.isPending}
        onConfirm={() => {
          cancel.mutate(null)
        }}
      />
      {authorising && salesDocumentId !== null ? (
        <AuthorisationDialog
          title={`Cancelar o pedido nº ${String(order.number)}`}
          description="O pedido está segurando mercadoria. Ela volta ao estoque, e um supervisor precisa autorizar."
          action="sales.cancel"
          targetType="sales_document"
          targetId={salesDocumentId}
          onAuthorised={(authorisationId) => {
            setAuthorising(false)
            cancel.mutate(authorisationId)
          }}
          onClose={() => {
            setAuthorising(false)
          }}
        />
      ) : null}
    </Section>
  )
}
